import React, { useState, useRef, useEffect } from 'react'
import { Grid, Typography, useTheme, Box, TextField, Button, CircularProgress, Card, CardContent, Stack, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Divider, Chip, Tabs, Tab, Paper, Stepper, Step, StepLabel,} from '@mui/material'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import BusinessIcon from '@mui/icons-material/Business'
import SendIcon from '@mui/icons-material/Send'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import WorkIcon from '@mui/icons-material/Work'
import EditIcon from '@mui/icons-material/Edit'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { useSelector } from 'react-redux'
import { CANDIDATE_TYPE_ENUM, PRACTICE_TYPE_NUMERICAL_ENUM } from 'data/enums'
import { useSpecialties } from 'data/layout/actions'
import { imageAddress } from 'data/helpers'
import HospitalInfo from './components/HospitalInfo'
import Campuses from './components/Campuses'
import Details from './components/Details'
import DescriptionConfigurator from './components/DescriptionConfigurator'
import JobPreferences from './components/JobPreferences'

// Configuration steps
const CONFIG_STEPS = {
  HOSPITAL_INFO: 'HOSPITAL_INFO',
  CAMPUSES: 'CAMPUSES',
  DETAILS: 'DETAILS',
  JOB_PREFERENCES: 'JOB_PREFERENCES',
  GENERATE_JOBS: 'GENERATE_JOBS'
}

// Details scope options
const DETAILS_SCOPE = {
  HOSPITAL_WIDE: 'hospital-wide',
  CAMPUS_SPECIFIC: 'campus_specific',
  JOB_SPECIFIC: 'job_specific'
}

const JobAssistant = () => {
  const theme = useTheme()
  const [prompt, setPrompt] = useState('')
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('hospital-info')
  
  // Configuration state
  const [configStep, setConfigStep] = useState(CONFIG_STEPS.HOSPITAL_INFO)
  const [completedSteps, setCompletedSteps] = useState(new Set([CONFIG_STEPS.HOSPITAL_INFO]))
  const [hospitalInfo, setHospitalInfo] = useState({
    name: '',
    image: '',
    description: '',
    address: '',
    city: '',
    state: ''
  })
  const [campuses, setCampuses] = useState([])
  const [jobDetails, setJobDetails] = useState({
    details: {
      pslfCompatible: { scope: DETAILS_SCOPE.HOSPITAL_WIDE, value: false, campusValues: {} },
      loanForgiveness: { scope: DETAILS_SCOPE.HOSPITAL_WIDE, value: false, campusValues: {} },
      visaSponsorship: { scope: DETAILS_SCOPE.HOSPITAL_WIDE, value: false, campusValues: {} },
      academicType: { scope: DETAILS_SCOPE.HOSPITAL_WIDE, value: '', campusValues: {} },
      area: { scope: DETAILS_SCOPE.HOSPITAL_WIDE, value: '', campusValues: {} }
    }
  })
  const [jobDescriptionPreferences, setJobDescriptionPreferences] = useState({
    includeHospitalDescription: { enabled: false, source: 'ai', customContent: '' },
    includeHospitalBenefits: { enabled: false, source: 'ai', customContent: '' },
    includeHospitalCulture: { enabled: false, source: 'ai', customContent: '' },
    includeLocationDetails: { enabled: false, source: 'ai', customContent: '' },
    includeCommunityInfo: { enabled: false, source: 'ai', customContent: '' },
    includeCostOfLiving: { enabled: false, source: 'ai', customContent: '' },
    includeJobRequirements: { enabled: false, source: 'ai', customContent: '' },
    includeCompensation: { enabled: false, source: 'ai', customContent: '' },
    includeSchedule: { enabled: false, source: 'ai', customContent: '' },
    includeGrowthOpportunities: { enabled: false, source: 'ai', customContent: '' },
    includeResearchOpportunities: { enabled: false, source: 'ai', customContent: '' },
    includeTeachingOpportunities: { enabled: false, source: 'ai', customContent: '' }
  })
  
  const [conversation, setConversation] = useState([
    { 
      type: 'ai', 
      content: 'Thank you for configuring your hospital information. What jobs can I add for you today?',
    }
  ])
  const [generatedJobs, setGeneratedJobs] = useState([])
  const [editingJob, setEditingJob] = useState(null)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [expandedJobId, setExpandedJobId] = useState(null)
  const messagesEndRef = useRef(null)
  const employerId = useSelector(state => state.identity.employerId)
  const employerUserId = useSelector(state => state.identity.employerUserId)
  const { specialties } = useSpecialties()
  const [jobQuestions, setJobQuestions] = useState({})
  const [jobQuestionsLoading, setJobQuestionsLoading] = useState({})

  // Update activeTab when configStep changes
  useEffect(() => {
    if (configStep === CONFIG_STEPS.GENERATE_JOBS) {
      setActiveTab('job-creation')
    } else {
      setActiveTab('hospital-info')
    }
  }, [configStep])

  // Configuration handlers
  const handleHospitalInfoSubmit = () => {
    setCompletedSteps(prev => new Set([...prev, CONFIG_STEPS.CAMPUSES]))
    setConfigStep(CONFIG_STEPS.CAMPUSES)
  }

  const handleHospitalInfoChange = (field, value) => {
    setHospitalInfo(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleAddCampus = () => {
    setCampuses(prev => [
      ...prev,
      {
        id: Date.now(),
        name: '',
        description: '',
        image: '',
        address: '',
        city: '',
        state: ''
      }
    ])
  }

  const handleCampusChange = (id, field, value) => {
    setCampuses(prev => prev.map(campus => 
      campus.id === id ? { ...campus, [field]: value } : campus
    ))
  }

  const handleRemoveCampus = (id) => {
    setCampuses(prev => prev.filter(campus => campus.id !== id))
  }

  const handleCampusesSubmit = () => {
    setCompletedSteps(prev => new Set([...prev, CONFIG_STEPS.DETAILS]))
    setConfigStep(CONFIG_STEPS.DETAILS)
  }

  const handleDetailsChange = (key, field, value) => {
    setJobDetails(prev => {
      // Create a new details object
      const newDetails = { ...prev.details }
      if (field === 'value' || field === 'scope') {
        newDetails[key] = {
          ...newDetails[key],
          [field]: value
        }
      } else if (field === 'campusValues') {
        newDetails[key] = {
          ...newDetails[key],
          campusValues: newDetails[key].campusValues || {}
        }
        newDetails[key].campusValues = value
      }
      return {
        details: newDetails
      }
    })
  }

  const handleDetailsSubmit = () => {
    setCompletedSteps(prev => new Set([...prev, CONFIG_STEPS.JOB_PREFERENCES]))
    setConfigStep(CONFIG_STEPS.JOB_PREFERENCES)
  }

  // Job Preferences Handlers
  const handleJobPreferencesChange = (key, field, value) => {
    setJobDescriptionPreferences(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value
      }
    }))
  }

  const fetchHospitalDescriptionFromInternet = async (type, hospitalName) => {
    try {
      const response = await fetch('http://localhost:5050/api/ai/hospital_descriptions_from_internet', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type,
          hospitalName
        }),
      })

      if (!response.ok) {
        throw new Error('Failed to fetch hospital description')
      }

      const data = await response.json()
      return data.description
    } catch (err) {
      console.error('Error fetching hospital description:', err)
      return null
    }
  }

  const handleJobPreferencesSubmit = () => {
    setCompletedSteps(prev => new Set([...prev, CONFIG_STEPS.GENERATE_JOBS]))
    setConfigStep(CONFIG_STEPS.GENERATE_JOBS)
  }

  const handleEditClick = (job) => {
    setEditingJob({ ...job })
    setEditDialogOpen(true)
  }

  const handleEditClose = () => {
    setEditingJob(null)
    setEditDialogOpen(false)
  }

  const handleEditSave = () => {
    setGeneratedJobs(prev => prev.map(job => 
      job.id === editingJob.id ? editingJob : job
    ))
    handleEditClose()
  }

  const handleEditChange = (field, value) => {
    setEditingJob(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [conversation])


  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!prompt.trim()) return

    setLoading(true)

    // Add user message to conversation
    setConversation(prev => [...prev, { type: 'user', content: prompt }])

    try {
      // Prepare description parameters based on job preferences
      const descriptionParameters = {}
      
      // Add enabled preferences to the parameters
      Object.entries(jobDescriptionPreferences).forEach(([key, value]) => {
        if (value.enabled) {
          descriptionParameters[key] = {
            source: value.source,
            content: value.customContent
          }
        }
      })

      const response = await fetch('http://localhost:5050/api/ai/create_job_json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          employerId,
          employerUserId,
          description: prompt,
          descriptionParameters,
          hospitalName: hospitalInfo.name
        }),
      })

      if (!response.ok) {
        throw new Error('Failed to generate job JSON')
      }

      const data = await response.json()
      
      // Create multiple job items based on the count
      const jobs = Array(data.Count).fill(null).map((_, index) => {
        const job = {
          id: `temp-${Date.now()}-${index}`,
          title: data.Title || `Position ${index + 1}`,
          status: 'Draft',
          // Map the API response fields to the job object
          city: data.City || hospitalInfo.city || '',
          state: data.State || hospitalInfo.state || '',
          salary: data.Salary || '',
          about: data.About || '',
          PracticeType: data.PracticeType || 1,
          CandidateTypeId: data.CandidateTypeId || 1,
          SpecialtyIds: data.SpecialtyIds || [],
          employer: {
            id: employerId,
            name: hospitalInfo.name || (data.EmployerName || 'Your Organization')
          }
        }
        return job
      })
      
      setGeneratedJobs(prev => [...prev, ...jobs])
      setActiveTab('job-creation') // Switch to the Jobs tab to show generated jobs
      
      // Add AI response to conversation
      setConversation(prev => [...prev, { 
        type: 'ai', 
        content: `I've generated ${data.Count} job positions based on your description. You can find them in the Jobs tab.`,
      }])
    } catch (err) {
      setConversation(prev => [...prev, { 
        type: 'ai', 
        content: 'Sorry, I encountered an error while generating the job JSON. Please try again.',
        error: true
      }])
    } finally {
      setLoading(false)
      setPrompt('')
    }
  }

  const handleJobQuestionSubmit = async (jobId, question) => {
    if (!question.trim()) return
    
    // Set loading state for this specific job
    setJobQuestionsLoading(prev => ({ ...prev, [jobId]: true }))
    
    try {
      const response = await fetch('http://localhost:5050/api/ai/job_info', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          jobId: jobId,
          candidatePrompt: question
        }),
      })

      if (!response.ok) {
        throw new Error('Failed to get job information')
      }

      const data = await response.json()
      
      // Update job-specific questions and answers
      setJobQuestions(prev => ({
        ...prev,
        [jobId]: [
          ...(prev[jobId] || []),
          { question, answer: data }
        ]
      }))
    } catch (err) {
      // Handle error - store the error in the job questions state
      setJobQuestions(prev => ({
        ...prev,
        [jobId]: [
          ...(prev[jobId] || []),
          { question, answer: "Sorry, I couldn't retrieve information about this job. Please try again." }
        ]
      }))
    } finally {
      setJobQuestionsLoading(prev => ({ ...prev, [jobId]: false }))
    }
  }

  const JobCard = ({ job }) => {
    const [jobQuestion, setJobQuestion] = useState('')
    const isExpanded = expandedJobId === job.id
    const isLoading = jobQuestionsLoading[job.id]
    const questions = jobQuestions[job.id] || []
    
    const handleExpandClick = (e) => {
      e.stopPropagation()
      setExpandedJobId(isExpanded ? null : job.id)
    }
    
    const handleQuestionSubmit = (e) => {
      e.preventDefault()
      e.stopPropagation() // Prevent triggering the card click
      handleJobQuestionSubmit(job.id, jobQuestion)
      setJobQuestion('')
    }
    
    return (
      <Card 
        sx={{ 
          width: '100%',
          borderRadius: 1,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          '&:hover': {
            boxShadow: '0 3px 8px rgba(0, 0, 0, 0.15)',
            transition: 'all 0.2s ease-in-out'
          }
        }}
      >
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Stack spacing={1.5}>
            {/* Job Title and Employer */}
            <Box 
              onClick={() => handleExpandClick()}
              sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                cursor: 'pointer'
              }}
            >
              <Box>
                <Typography variant='subtitle1' sx={{ color: theme.palette.primary.main, fontWeight: 'medium', mb: 0.5 }}>
                  {job.title}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BusinessIcon fontSize="small" color='action' />
                  <Typography variant='body2' color='text.secondary'>
                    {job.employer.name}
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Chip
                  label={PRACTICE_TYPE_NUMERICAL_ENUM[job.PracticeType]}
                  size='small'
                  variant='outlined'
                  sx={{ height: '24px' }}
                />
                <IconButton 
                  size="small" 
                  onClick={(e) => {
                    e.stopPropagation()
                    handleEditClick(job)
                  }}
                  sx={{ mr: 1 }}
                >
                  <EditIcon />
                </IconButton>
                <IconButton 
                  size="small"
                  onClick={handleExpandClick}
                  aria-expanded={isExpanded}
                  aria-label="show more"
                >
                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
            </Box>

            {/* Location and Salary - Combined Row */}
            <Box 
              onClick={() => handleExpandClick()}
              sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationOnIcon fontSize="small" color='action' />
                <Typography variant='body2'>
                  {job.city || 'Location not specified'}, {job.state || 'State not specified'}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WorkIcon fontSize="small" color='action' />
                <Typography variant='body2'>
                  {job.CandidateTypeId ? (CANDIDATE_TYPE_ENUM[job.CandidateTypeId] || job.CandidateTypeId) : 'Candidate type not specified'}
                </Typography>
              </Box>
            </Box>

            {/* Specialties and Description - Row */}
            <Box 
              onClick={() => handleExpandClick()}
              sx={{ 
                display: 'flex', 
                gap: 2,
                alignItems: 'flex-start',
                cursor: 'pointer'
              }}
            >
              {/* Specialties Column */}
              <Box sx={{ minWidth: '40%' }}>
                <Typography variant='caption' sx={{ color: theme.palette.text.secondary, display: 'block', mb: 0.5 }}>
                  Specialties
                </Typography>
                <Stack direction='row' spacing={0.5} flexWrap='wrap' gap={0.5}>
                  {job.SpecialtyIds?.slice(0, 2).map((specialtyId) => {
                    const specialty = specialties.find(s => s.id.toString() === specialtyId.toString())
                    return specialty ? (
                      <Chip
                        key={specialtyId}
                        label={specialty.name}
                        size='small'
                        sx={{ height: '20px', fontSize: '0.7rem' }}
                      />
                    ) : null
                  })}
                  {job.SpecialtyIds?.length > 2 && (
                    <Chip
                      label={`+${job.SpecialtyIds.length - 2}`}
                      size='small'
                      variant='outlined'
                      sx={{ height: '20px', fontSize: '0.7rem' }}
                    />
                  )}
                </Stack>
              </Box>
              
              {/* Description Column */}
              {job.about && (
                <Box sx={{ flex: 1 }}>
                  <Typography variant='caption' sx={{ color: theme.palette.text.secondary, display: 'block', mb: 0.5 }}>
                    Description
                  </Typography>
                  <Typography variant='body2' sx={{ 
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    fontSize: '0.8rem',
                    lineHeight: 1.3
                  }}>
                    {job.about.length > 90 ? `${job.about.substring(0, 90)}...` : job.about}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Expanded Details Section */}
            {isExpanded && (
              <>
                <Divider sx={{ my: 0.5 }} />
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body1" fontWeight="medium" gutterBottom>
                    Full Description
                  </Typography>
                  <Typography variant="body2" paragraph>
                    {job.about || "No detailed description available."}
                  </Typography>
                </Box>
              </>
            )}

            {/* Questions Section */}
            <Divider sx={{ my: 0.5 }} />
            
            {/* Question form - always visible */}
            <Box 
              component="form" 
              onSubmit={handleQuestionSubmit}
              onClick={(e) => e.stopPropagation()}
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <TextField
                size="small"
                placeholder="Ask about this job..."
                value={jobQuestion}
                onChange={(e) => setJobQuestion(e.target.value)}
                InputProps={{
                  startAdornment: <HelpOutlineIcon fontSize="small" color="action" sx={{ mr: 0.5 }} />,
                }}
                sx={{ 
                  flexGrow: 1,
                  '& .MuiOutlinedInput-root': {
                    height: '36px',
                    fontSize: '0.85rem'
                  }
                }}
              />
              <Button
                type="submit"
                variant="contained"
                size="small"
                disabled={isLoading || !jobQuestion.trim()}
                sx={{ minWidth: '36px', height: '36px', p: 0 }}
              >
                {isLoading ? <CircularProgress size={16} /> : <SendIcon fontSize="small" />}
              </Button>
            </Box>
            
            {/* Previous questions - collapsed by default */}
            {questions.length > 0 && (
              <Box sx={{ backgroundColor: theme.palette.grey[50], p: 1, borderRadius: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant='caption' sx={{ fontWeight: 'bold', color: theme.palette.text.secondary }}>
                    Most recent question
                  </Typography>
                  {questions.length > 1 && (
                    <Typography variant='caption' sx={{ color: theme.palette.primary.main, cursor: 'pointer' }} onClick={(e) => {
                      e.stopPropagation()
                      handleExpandClick()
                    }}>
                      View all {questions.length} →
                    </Typography>
                  )}
                </Box>
                <Typography variant='body2' sx={{ fontWeight: 'medium', fontSize: '0.8rem' }}>
                  Q: {questions[questions.length - 1].question}
                </Typography>
                <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                  A: {questions[questions.length - 1].answer}
                </Typography>
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>
    )
  }

  // Render configuration widget
  const renderConfigWidget = () => {
    switch (configStep) {
      case CONFIG_STEPS.HOSPITAL_INFO:
        return (
          <HospitalInfo
            hospitalInfo={hospitalInfo}
            handleHospitalInfoChange={handleHospitalInfoChange}
            handleHospitalInfoSubmit={handleHospitalInfoSubmit}
          />
        )
      case CONFIG_STEPS.CAMPUSES:
        return (
          <Campuses
            campuses={campuses}
            handleAddCampus={handleAddCampus}
            handleCampusChange={handleCampusChange}
            handleRemoveCampus={handleRemoveCampus}
            handleCampusesSubmit={handleCampusesSubmit}
          />
        )
      case CONFIG_STEPS.DETAILS:
        return (
          <Details
            jobDetails={jobDetails}
            handleDetailsChange={handleDetailsChange}
            handleDetailsSubmit={handleDetailsSubmit}
            DETAILS_SCOPE={DETAILS_SCOPE}
            campuses={campuses}
          />
        )
      case CONFIG_STEPS.JOB_PREFERENCES:
        return (
          <JobPreferences
            jobDescriptionPreferences={jobDescriptionPreferences}
            handleJobPreferencesChange={handleJobPreferencesChange}
            handleJobPreferencesSubmit={handleJobPreferencesSubmit}
            setConfigStep={setConfigStep}
            CONFIG_STEPS={CONFIG_STEPS}
            hospitalInfo={hospitalInfo}
            campuses={campuses}
            jobDetails={jobDetails}
            fetchHospitalDescriptionFromInternet={fetchHospitalDescriptionFromInternet}
          />
        )
      case CONFIG_STEPS.GENERATE_JOBS:
        return (
          <DescriptionConfigurator
            prompt={prompt}
            setPrompt={setPrompt}
            handleSubmit={handleSubmit}
            loading={loading}
            conversation={conversation}
            messagesEndRef={messagesEndRef}
            scrollToBottom={scrollToBottom}
            jobDescriptionPreferences={jobDescriptionPreferences}
            setConfigStep={setConfigStep}
            CONFIG_STEPS={CONFIG_STEPS}
          />
        )
      default:
        return null
    }
  }

  const handleTabChange = (event, newValue) => {
    // Only allow switching to job-creation tab if we're in the GENERATE_JOBS step
    if (newValue === 'job-creation' && configStep !== CONFIG_STEPS.GENERATE_JOBS) {
      return
    }
    setActiveTab(newValue)
  }

  const handleStepClick = (step) => {
    const stepIndex = Object.values(CONFIG_STEPS).indexOf(step)
    const currentStepIndex = Object.values(CONFIG_STEPS).indexOf(configStep)
    
    // Only allow navigation to completed steps or the next available step
    if (completedSteps.has(step) || stepIndex === currentStepIndex + 1) {
      setConfigStep(step)
    }
  }

  return (
    <Grid
      container
      direction='column'
      alignItems='center'
      sx={{
        minHeight: '100vh',
        padding: '40px 20px',
        backgroundColor: theme.palette.primary.light,
      }}
    >
      {/* Description */}
      <Typography
        variant='body1'
        sx={{ 
          maxWidth: '600px', 
          color: theme.palette.secondary.dark,
          textAlign: 'left',
          mb: 4
        }}
      >
        <AutoAwesomeIcon sx={{ 
          color: theme.palette.secondary.main,
          mr: 1,
          animation: 'sparkle 1.5s infinite alternate',
          '@keyframes sparkle': {
            '0%': { opacity: 0.8, transform: 'scale(1)' },
            '100%': { opacity: 1, transform: 'scale(1.2)' }
          }
        }}/> Welcome to the Job Assistant! Let's configure your hospital and create job positions together!
      </Typography>

      {/* Configuration Stepper */}
      <Stepper 
        activeStep={Object.values(CONFIG_STEPS).indexOf(configStep)} 
        sx={{ width: '100%', maxWidth: '800px', mb: 4 }}
      >
        {Object.values(CONFIG_STEPS).map((step) => (
          <Step 
            key={step} 
            completed={completedSteps.has(step)}
            onClick={() => handleStepClick(step)}
            sx={{ 
              cursor: completedSteps.has(step) || step === configStep ? 'pointer' : 'default',
              '& .MuiStepLabel-root': {
                cursor: completedSteps.has(step) || step === configStep ? 'pointer' : 'default',
              }
            }}
          >
            <StepLabel>{step.replace(/_/g, ' ')}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Main Content Grid */}
      <Grid container spacing={4} sx={{ width: '100%', maxWidth: '1600px' }}>
        {/* Left Panel - Generated Jobs */}
        <Grid item xs={12} md={6}>
          <Paper 
            elevation={3} 
            sx={{ 
              height: '80vh', 
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 2
            }}
          >
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs 
                value={activeTab} 
                onChange={handleTabChange}
                aria-label="job assistant tabs"
                sx={{ 
                  '& .MuiTab-root': { 
                    fontWeight: 'medium',
                    textTransform: 'none',
                    fontSize: '0.9rem'
                  }
                }}
              >
                <Tab label="Hospital Info" value="hospital-info" />
                <Tab label="Job Creation" value="job-creation" disabled={configStep !== CONFIG_STEPS.GENERATE_JOBS} />
              </Tabs>
            </Box>
            <Box sx={{ 
              flexGrow: 1, 
              overflow: 'auto',
              p: 3,
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: theme.palette.grey[200],
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: theme.palette.grey[400],
                borderRadius: '4px',
                '&:hover': {
                  background: theme.palette.grey[500],
                },
              },
            }}>
              {activeTab === 'hospital-info' ? (
                <Box sx={{ height: '100%', overflow: 'auto' }}>
                  {hospitalInfo.name ? (
                    <Box>
                      {/* Hospital Information - Always show once entered */}
                      {hospitalInfo.name && (
                        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <BusinessIcon color="primary" sx={{ mr: 2, fontSize: 28 }} />
                            <Typography variant="h5" sx={{ fontWeight: 'medium' }}>
                              {hospitalInfo.name}
                            </Typography>
                          </Box>
                          
                          <Divider sx={{ mb: 2 }} />

                          {hospitalInfo.image && (
                            <Box sx={{ mb: 2, textAlign: 'center' }}>
                              <img 
                                src={typeof hospitalInfo.image === 'string' ? hospitalInfo.image : imageAddress(hospitalInfo.image)}
                                alt={hospitalInfo.name}
                                style={{ 
                                  maxWidth: '100%',
                                  maxHeight: '200px',
                                  objectFit: 'contain'
                                }}
                                onError={(e) => {
                                  console.error('Image failed to load:', e)
                                  e.target.style.display = 'none'
                                }}
                              />
                            </Box>
                          )}
                          
                          {hospitalInfo.description && (
                            <Typography variant="body1" paragraph>
                              {hospitalInfo.description}
                            </Typography>
                          )}
                          
                          {(hospitalInfo.city || hospitalInfo.state) && (
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <LocationOnIcon color="action" sx={{ mr: 1 }} />
                              <Typography variant="body1">
                                <strong>Location:</strong> {hospitalInfo.city || "City not specified"}, {hospitalInfo.state || "State not specified"}
                              </Typography>
                            </Box>
                          )}
                          
                          {hospitalInfo.address && (
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <LocationOnIcon color="action" sx={{ mr: 1 }} />
                              <Typography variant="body1">
                                <strong>Address:</strong> {hospitalInfo.address}
                              </Typography>
                            </Box>
                          )}
                        </Paper>
                      )}
                      
                      {/* Campuses Section - Show when in CAMPUSES step or later */}
                      {configStep !== CONFIG_STEPS.HOSPITAL_INFO && campuses.length > 0 && campuses.some(campus => campus.name || campus.description || campus.image || campus.city || campus.state) && (
                        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <BusinessIcon color="primary" sx={{ mr: 2, fontSize: 28 }} />
                            <Typography variant="h5" sx={{ fontWeight: 'medium' }}>
                              Campuses
                            </Typography>
                          </Box>
                          
                          <Divider sx={{ mb: 2 }} />
                          
                          <Grid container spacing={2}>
                            {campuses.filter(campus => campus.name || campus.description || campus.image || campus.city || campus.state).map((campus) => (
                              <Grid item xs={12} sm={6} key={campus.id}>
                                <Card variant="outlined" sx={{ height: '100%' }}>
                                  <CardContent>
                                    {campus.name && (
                                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium' }}>
                                        {campus.name}
                                      </Typography>
                                    )}

                                    {campus.image && (
                                      <Box sx={{ mb: 2, textAlign: 'center' }}>
                                        <img 
                                          src={typeof campus.image === 'string' ? campus.image : imageAddress(campus.image)}
                                          alt={campus.name || 'Campus'}
                                          style={{ 
                                            maxWidth: '100%',
                                            maxHeight: '150px',
                                            objectFit: 'contain'
                                          }}
                                          onError={(e) => {
                                            console.error('Image failed to load:', e)
                                            e.target.style.display = 'none'
                                          }}
                                        />
                                      </Box>
                                    )}
                                    
                                    {campus.description && (
                                      <Typography variant="body2" color="text.secondary" paragraph>
                                        {campus.description}
                                      </Typography>
                                    )}
                                    
                                    {(campus.city || campus.state) && (
                                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                        <LocationOnIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                                        <Typography variant="body2">
                                          {campus.city || "City not specified"}, {campus.state || "State not specified"}
                                        </Typography>
                                      </Box>
                                    )}
                                  </CardContent>
                                </Card>
                              </Grid>
                            ))}
                          </Grid>
                        </Paper>
                      )}

                      {/* Job Details Section - Show when in DETAILS step or later */}
                      {configStep !== CONFIG_STEPS.HOSPITAL_INFO && configStep !== CONFIG_STEPS.CAMPUSES && Object.entries(jobDetails.details).some(([, detail]) => detail.value || detail.scope) && (
                        <Paper sx={{ p: 2, mb: 2 }}>
                          <Typography variant="h6" gutterBottom>
                            Job Details
                          </Typography>
                          <Grid container spacing={2}>
                            {Object.entries(jobDetails.details)
                              .filter(([, detail]) => detail.value || detail.scope)
                              .map(([key, detail]) => {
                                // Convert key to user-friendly label
                                const getDetailLabel = (key) => {
                                  const labels = {
                                    pslfCompatible: 'PSLF Compatible',
                                    loanForgiveness: 'Loan Forgiveness',
                                    visaSponsorship: 'Visa Sponsorship',
                                    academicType: 'Academic Type',
                                    area: 'Area Type'
                                  }
                                  return labels[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())
                                }
                                
                                // Convert scope to user-friendly text
                                const getScopeLabel = (scope) => {
                                  const scopes = {
                                    'hospital-wide': 'Hospital-wide',
                                    'campus_specific': 'Campus-specific',
                                    'job_specific': 'Job-specific'
                                  }
                                  return scopes[scope] || scope
                                }
                                
                                // Format value based on detail type
                                const formatValue = (key, value) => {
                                  if (key === 'pslfCompatible' || key === 'loanForgiveness' || key === 'visaSponsorship') {
                                    return value ? 'Yes' : 'No'
                                  } else if (key === 'academicType') {
                                    // You might want to add a mapping for academic types here
                                    return value || 'Not specified'
                                  } else if (key === 'area') {
                                    // You might want to add a mapping for area types here
                                    return value || 'Not specified'
                                  } else {
                                    return value || 'Not specified'
                                  }
                                }
                                
                                return (
                                  <Grid item xs={12} sm={6} key={key}>
                                    <Card variant="outlined">
                                      <CardContent>
                                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium' }}>
                                          {getDetailLabel(key)}
                                        </Typography>
                                        {detail.scope && (
                                          <Typography variant="body2" color="text.secondary">
                                            <strong>Scope:</strong> {getScopeLabel(detail.scope)}
                                          </Typography>
                                        )}
                                        {detail.value !== undefined && detail.value !== '' && (
                                          <Typography variant="body2">
                                            <strong>Value:</strong> {formatValue(key, detail.value)}
                                          </Typography>
                                        )}
                                      </CardContent>
                                    </Card>
                                  </Grid>
                                )
                              })}
                          </Grid>
                        </Paper>
                      )}

                      {/* Description Configurator Section - Show when in JOB_PREFERENCES step or later */}
                      {configStep !== CONFIG_STEPS.HOSPITAL_INFO && configStep !== CONFIG_STEPS.CAMPUSES && configStep !== CONFIG_STEPS.DETAILS && Object.entries(jobDescriptionPreferences).some(([, value]) => value !== undefined) && (
                        <Paper variant="outlined" sx={{ p: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <WorkIcon color="primary" sx={{ mr: 2, fontSize: 28 }} />
                            <Typography variant="h5" sx={{ fontWeight: 'medium' }}>
                              Description Configurator
                            </Typography>
                          </Box>
                          
                          <Divider sx={{ mb: 2 }} />
                          
                          <Grid container spacing={2}>
                            {Object.entries(jobDescriptionPreferences)
                              .filter(([, value]) => value !== undefined)
                              .map(([key, value]) => (
                                <Grid item xs={12} sm={6} key={key}>
                                  <Card variant="outlined">
                                    <CardContent>
                                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium' }}>
                                        {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                      </Typography>
                                      <Typography variant="body2">
                                        {value.enabled ? 'Yes' : 'No'}
                                      </Typography>
                                    </CardContent>
                                  </Card>
                                </Grid>
                            ))}
                          </Grid>
                        </Paper>
                      )}
                </Box>
              ) : (
                    <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                      <BusinessIcon color="action" sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                      <Typography variant="h6" gutterBottom>
                        No Hospital Information Available
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        Complete the Hospital Info step to configure your hospital details.
                      </Typography>
                    </Paper>
                  )}
                </Box>
              ) : (
                <Stack spacing={2}>
                  {generatedJobs.length > 0 ? (
                    generatedJobs.map((job) => (
                      <JobCard key={job.id} job={job} />
                    ))
                  ) : (
                    <Box sx={{ 
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                      height: '100%',
                      color: 'text.secondary'
                    }}>
                      <WorkIcon sx={{ fontSize: 60, mb: 2, opacity: 0.5 }} />
                      <Typography variant="h6" gutterBottom>
                    No jobs generated yet
                  </Typography>
                      <Typography variant="body2" align="center">
                        Complete the configuration steps and enter a job description to generate job listings.
                  </Typography>
                </Box>
                  )}
                </Stack>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Right Panel - Configuration and Chat */}
        <Grid item xs={12} md={6}>
          <Paper 
            elevation={3} 
            sx={{
              height: '80vh', 
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 2
            }}
          >
            <Box sx={{ 
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}>
              <Box sx={{ 
                flexGrow: 1, 
                overflow: 'auto', 
                p: 3,
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: theme.palette.grey[200],
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: theme.palette.grey[400],
                  borderRadius: '4px',
                  '&:hover': {
                    background: theme.palette.grey[500],
                  },
                },
              }}>
                {renderConfigWidget()}
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Edit Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={handleEditClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Job</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
            <TextField
              fullWidth
                label="Title"
              value={editingJob?.title || ''}
              onChange={(e) => handleEditChange('title', e.target.value)}
            />
            </Grid>
            <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
                label="City"
              value={editingJob?.city || ''}
              onChange={(e) => handleEditChange('city', e.target.value)}
            />
            </Grid>
            <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
                label="State"
              value={editingJob?.state || ''}
              onChange={(e) => handleEditChange('state', e.target.value)}
            />
            </Grid>
            <Grid item xs={12}>
            <TextField
              fullWidth
                label="Salary"
                value={editingJob?.salary || ''}
                onChange={(e) => handleEditChange('salary', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="About"
              value={editingJob?.about || ''}
              onChange={(e) => handleEditChange('about', e.target.value)}
                multiline
                rows={4}
            />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose}>Cancel</Button>
          <Button onClick={handleEditSave} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Grid>
  )
}

export default JobAssistant 